import React from "react"
import Badge from "../../../components/ui/badge/badge"

/**
 * Shared classification badge — single source of truth used by both the
 * email-row table cell and the email-drawer metadata grid.
 *
 * Three render states, in priority order:
 *
 *   1. Truly unclassified → amber "Unclassified" badge.
 *   2. UNCLASSIFIED + a `classification_suggested` value → amber
 *      "MAYBE: <suggested>" badge. Communicates "the model couldn't match any
 *      of the supplied categories but proposed this one as a fit".
 *   3. A real dotted classification key → badge coloured by its top-level
 *      domain (`business.*` and `booking.*` are positive green;
 *      `marketing.*` is informational blue; `personal.*` and `financial.*`
 *      are negative red; `internal.*` and `automated.*` are special purple).
 */
export default function ClassificationBadge({ comm }) {
	const classification = comm?.classification
	const suggested = comm?.classification_suggested

	const isUnclassified = !classification || classification === "UNCLASSIFIED"

	if (isUnclassified && suggested) {
		return (
			<Badge
				type=""
				label={`MAYBE: ${suggested}`}
			/>
		)
	}

	if (isUnclassified) {
		return (
			<Badge
				type=""
				label="Unclassified"
			/>
		)
	}

	const domain = classification.split(".")[0]
	const type = DOMAIN_BADGE_TYPES[domain] || ""
	return (
		<Badge
			type={type}
			label={classification}
		/>
	)
}

/**
 * Domain → badge colour map. The palette is deliberately structured so each
 * colour carries a consistent meaning across the table:
 *
 *   green   → customer-facing relationship            (business.*)
 *   blue    → booking lifecycle, our own process      (booking.*)
 *   amber   → external party, pay attention           (supplier.*)
 *   red     → sensitive, handle with care             (personal.*)
 *   purple  → file / process / behind the scenes      (financial.*, internal.*, automated.*)
 *   grey    → noise — skim or ignore                  (marketing.*, unclassified/MAYBE)
 */
const DOMAIN_BADGE_TYPES = {
	enquiry: "POSITIVE",
	business: "POSITIVE",
	booking: "INFO",
	supplier: "AMBER",
	marketing: "",
	personal: "NEGATIVE",
	financial: "SPECIAL",
	internal: "SPECIAL",
	automated: "SPECIAL",
}
